/* =========================================================
   LEVEYN – MAIN JS 2.0

   Inhalt
    1  Hilfsfunktionen
    2  Header (mitführen, ausblenden, Höhe messen)
    3  Suche, Aufklappmenüs
    4  Einblenden beim Scrollen
    5  Titelbild-Slideshow
    6  Slider (Produkte, Kategorien)
    7  Kundenstimmen
    8  Automatischer Bildwechsel auf Produktkarten (Handy)
    9  Warenkorb: Zähler, Schnellkauf, Warenkorb-Fenster
   10  Kategorieseite: Sortierung
   11  Produktseite: Galerie, Varianten, Kaufen
   12  Warenkorb-Seite
   13  Seite "Stornierung, Widerruf und Rückgabe": Reiter
   ========================================================= */

(function () {
  'use strict';

  /* =======================================================
     1  HILFSFUNKTIONEN
     ======================================================= */

  var config = window.Leveyn || {};
  var strings = config.strings || {};
  var root = (config.routes && config.routes.root) || '/';
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var motionOff = reduceMotion || document.documentElement.classList.contains('no-motion');

  /* Jeder Baustein läuft für sich: ein Fehler in einem Teil
     (z. B. durch eine App) legt nicht den Rest lahm. */
  function run(module) {
    try {
      return module();
    } catch (error) {
      if (window.console && console.error) console.error('Leveyn:', error);
      return null;
    }
  }

  function $(selector, scope) {
    return (scope || document).querySelector(selector);
  }

  function $$(selector, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* Ältere Browser kennen addEventListener an Media-Queries noch nicht */
  function onMediaChange(query, handler) {
    if (query.addEventListener) query.addEventListener('change', handler);
    else if (query.addListener) query.addListener(handler);
  }

  /* Seitlich scrollen – weich, wo der Browser es kann */
  function scrollX(element, left, smooth) {
    try {
      element.scrollTo({ left: left, behavior: smooth && !reduceMotion ? 'smooth' : 'auto' });
    } catch (error) {
      element.scrollLeft = left;
    }
  }

  function pad(number) {
    return (number < 10 ? '0' : '') + number;
  }

  /* Geldbetrag im Format des Shops (z. B. "€44,99") */
  function formatMoney(cents) {
    var format = config.moneyFormat || '€{{amount_with_comma_separator}}';
    var value = (parseInt(cents, 10) || 0) / 100;

    function withDelimiters(number, precision, thousands, decimal) {
      var parts = number.toFixed(precision).split('.');
      var whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      return whole + (parts[1] ? decimal + parts[1] : '');
    }

    return format.replace(/\{\{\s*(\w+)\s*\}\}/, function (match, key) {
      switch (key) {
        case 'amount':
          return withDelimiters(value, 2, ',', '.');
        case 'amount_no_decimals':
          return withDelimiters(value, 0, ',', '.');
        case 'amount_with_comma_separator':
          return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDelimiters(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return withDelimiters(value, 2, "'", '.');
        case 'amount_with_space_separator':
          return withDelimiters(value, 2, ' ', ',');
        default:
          return withDelimiters(value, 2, '.', ',');
      }
    });
  }

  function postJson(path, body) {
    return fetch(root + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) {
          /* Shopify liefert hier description/message, z. B. "ausverkauft" */
          throw new Error(data.description || data.message || strings.error || 'Fehler');
        }
        return data;
      });
    });
  }

  function getCart() {
    return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } }).then(function (response) {
      return response.json();
    });
  }

  /* Fokus innerhalb eines geöffneten Fensters halten */
  function trapFocus(container, event) {
    if (event.key !== 'Tab') return;
    var focusable = $$('a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])', container).filter(function (el) {
      return el.offsetParent !== null;
    });
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }


  /* =======================================================
     2  HEADER
     ======================================================= */

  var header = $('[data-header]');

  run(function initHeader() {
    if (!header) return;

    function measure() {
      document.documentElement.style.setProperty('--header-total', header.offsetHeight + 'px');
    }

    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('load', measure);
    if ('ResizeObserver' in window) {
      new ResizeObserver(measure).observe(header);
    }

    var sticky = header.classList.contains('is-sticky');
    var desktop = window.matchMedia('(min-width: 990px)');
    /* Startseite mit Kategorien als Vollbild (nur Computer) */
    var stage = $('[data-home-stage]');
    var lastY = window.scrollY;
    var ticking = false;

    function update() {
      var y = window.scrollY;

      /* Solange die Vollbild-Kategorien unter dem Header liegen, bleibt er
         durchsichtig und zeigt links keine Kategorien. */
      var overStage = false;
      if (stage && desktop.matches) {
        overStage = stage.getBoundingClientRect().bottom > header.offsetHeight;
      }
      header.classList.toggle('is-over-stage', overStage);
      header.classList.toggle('is-scrolled', overStage ? false : y > 24);

      if (sticky) {
        if (desktop.matches) {
          /* Computer: Der Header bleibt immer sichtbar und scrollt mit. */
          header.classList.remove('is-hidden');
        } else {
          /* Handy und Tablet: beim Scrollen nach unten ausblenden, nach oben sofort zeigen */
          var goingDown = y > lastY;
          var locked = header.classList.contains('is-open') || document.body.classList.contains('lv-is-locked');
          if (!locked && Math.abs(y - lastY) > 6) {
            header.classList.toggle('is-hidden', goingDown && y > 320);
          }
        }
      }

      lastY = y;
      ticking = false;
    }

    window.addEventListener(
      'scroll',
      function () {
        if (!ticking) {
          ticking = true;
          window.requestAnimationFrame(update);
        }
      },
      { passive: true }
    );

    window.addEventListener('resize', update);
    onMediaChange(desktop, update);

    update();
  });


  /* =======================================================
     3  SUCHE, AUFKLAPPMENÜS
     ======================================================= */

  run(function initSearch() {
    var panel = $('[data-search]');
    var button = $('[data-search-open]');
    if (!panel || !button || !header) return;

    var input = $('input[type="search"]', panel);

    function open() {
      panel.hidden = false;
      header.classList.add('is-open');
      button.setAttribute('aria-expanded', 'true');
      window.setTimeout(function () {
        if (input) input.focus();
      }, 60);
    }

    function close(returnFocus) {
      panel.hidden = true;
      header.classList.remove('is-open');
      button.setAttribute('aria-expanded', 'false');
      if (returnFocus) button.focus();
    }

    button.addEventListener('click', function (event) {
      event.preventDefault();
      if (panel.hidden) open();
      else close(true);
    });

    $$('[data-search-close]', panel).forEach(function (el) {
      el.addEventListener('click', function () {
        close(true);
      });
    });

    panel.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') close(true);
    });

    document.addEventListener('click', function (event) {
      if (panel.hidden) return;
      if (event.target.closest('[data-search]') || event.target.closest('[data-search-open]')) return;
      close(false);
    });
  });

  run(function initDropdowns() {
    var toggles = $$('.lv-nav__item.has-dropdown > .dropdown-toggle');
    if (!toggles.length) return;

    function closeAll(except) {
      $$('.lv-nav__item.dropdown-open').forEach(function (item) {
        if (item === except) return;
        item.classList.remove('dropdown-open');
        var link = $('.dropdown-toggle', item);
        if (link) link.setAttribute('aria-expanded', 'false');
      });
    }

    toggles.forEach(function (toggle) {
      toggle.addEventListener('click', function (event) {
        event.preventDefault();
        var parent = toggle.closest('.lv-nav__item');
        var isOpen = parent.classList.contains('dropdown-open');
        closeAll(parent);
        parent.classList.toggle('dropdown-open', !isOpen);
        toggle.setAttribute('aria-expanded', String(!isOpen));
      });
    });

    document.addEventListener('click', function (event) {
      if (!event.target.closest('.lv-nav__item.has-dropdown')) closeAll();
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      var open = $('.lv-nav__item.dropdown-open');
      if (!open) return;
      var toggle = $('.dropdown-toggle', open);
      closeAll();
      if (toggle) toggle.focus();
    });
  });


  /* =======================================================
     4  EINBLENDEN BEIM SCROLLEN
     Elemente mit data-reveal bekommen die Klasse "is-visible",
     sobald sie ins Bild kommen. Ohne Animation (Einstellung
     oder "Bewegung reduzieren") sind sie von Anfang an sichtbar.
     ======================================================= */

  run(function initReveal() {
    var elements = $$('[data-reveal]');
    if (!elements.length) {
      window.LeveynReady = true;
      return;
    }

    /* Meldet dem kleinen Sicherheits-Script im <head>, dass alles läuft */
    window.LeveynReady = true;

    if (motionOff || !('IntersectionObserver' in window)) {
      elements.forEach(function (el) {
        el.classList.add('is-visible');
      });
      return;
    }

    /* Beobachtet wird nicht immer das Element selbst:
       - Karten in einem Slider erscheinen gemeinsam, sobald der Slider ins
         Bild kommt – auch die, die noch rechts außerhalb liegen.
       - Bilder mit Vorhang-Effekt sind anfangs vollständig beschnitten und
         würden vom Browser nie als "sichtbar" gemeldet. Für sie wird das
         umgebende Element beobachtet. */
    var groups = [];

    function groupFor(target) {
      for (var i = 0; i < groups.length; i++) {
        if (groups[i].target === target) return groups[i];
      }
      var group = { target: target, items: [] };
      groups.push(group);
      return group;
    }

    elements.forEach(function (el) {
      var target = el.closest('[data-slider-track]');
      if (!target) target = el.getAttribute('data-reveal') === 'image' ? el.parentElement : el;
      groupFor(target).items.push(el);
    });

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          groupFor(entry.target).items.forEach(function (el) {
            el.classList.add('is-visible');
          });
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -6% 0px' }
    );

    groups.forEach(function (group) {
      observer.observe(group.target);
    });

    /* Sicherheitsnetz: nichts bleibt dauerhaft unsichtbar */
    window.addEventListener('load', function () {
      window.setTimeout(function () {
        elements.forEach(function (el) {
          var rect = el.getBoundingClientRect();
          if (rect.top < window.innerHeight && rect.bottom > 0) el.classList.add('is-visible');
        });
      }, 1200);
    });
  });


  /* Große Zahl im Abschnitt "Stempel": wandert beim Scrollen leicht mit */
  run(function initDrift() {
    var elements = $$('[data-drift]');
    if (!elements.length || motionOff) return;

    var ticking = false;

    function update() {
      var viewport = window.innerHeight;
      elements.forEach(function (el) {
        var rect = el.parentElement.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > viewport) return;
        /* -1 (unten im Bild) bis 1 (oben im Bild) */
        var progress = (viewport / 2 - (rect.top + rect.height / 2)) / (viewport / 2 + rect.height / 2);
        el.style.setProperty('--drift', (progress * -28).toFixed(1));
      });
      ticking = false;
    }

    window.addEventListener(
      'scroll',
      function () {
        if (!ticking) {
          ticking = true;
          window.requestAnimationFrame(update);
        }
      },
      { passive: true }
    );
    update();
  });


  /* =======================================================
     5  TITELBILD-SLIDESHOW
     - Am Handy laufen alle Fotos, am Computer nur die mit
       Computer-Foto.
     - Pausiert, wenn der Tab nicht sichtbar ist.
     - Bei "Bewegung reduzieren" wechseln die Fotos weiterhin,
       nur ohne Überblendung und Zoom (das regelt die CSS).
     ======================================================= */

  function initHero() {
    var hero = $('[data-hero-slideshow]:not([data-ready])');
    if (!hero) return;
    hero.setAttribute('data-ready', 'true');

    var slides = $$('[data-hero-slide]', hero);
    var dots = $$('[data-hero-dot]', hero);
    var ui = $('[data-hero-ui]', hero);
    var currentLabel = $('[data-hero-current]', hero);
    var totalLabel = $('[data-hero-total]', hero);
    if (!slides.length) return;

    var interval = (parseFloat(hero.getAttribute('data-interval')) || 6) * 1000;
    var desktop = window.matchMedia('(min-width: 750px)');
    var current = -1;
    var timer = null;
    var list = [];

    /* Welche Fotos gelten in der aktuellen Ansicht? (Indizes in slides) */
    function buildList() {
      list = [];
      slides.forEach(function (slide, i) {
        var mobileOnly = slide.classList.contains('lv-hero__slide--mobile-only');
        if (desktop.matches && mobileOnly) return;
        list.push(i);
      });
      dots.forEach(function (dot, i) {
        dot.classList.toggle('is-hidden', list.indexOf(i) === -1);
      });
      if (ui) ui.classList.toggle('is-single', list.length < 2);
      if (totalLabel) totalLabel.textContent = pad(list.length);
    }

    function restartProgress() {
      hero.classList.remove('is-playing');
      /* Animation neu starten */
      void hero.offsetWidth;
      if (list.length > 1) hero.classList.add('is-playing');
    }

    function activate(index) {
      slides.forEach(function (slide, k) {
        slide.classList.toggle('is-active', k === index);
      });
      dots.forEach(function (dot, k) {
        dot.classList.toggle('is-active', k === index);
      });
      current = index;
      if (currentLabel) currentLabel.textContent = pad(list.indexOf(index) + 1);
      var image = $('img', slides[index]);
      if (image && image.loading === 'lazy') image.loading = 'eager';
      restartProgress();
    }

    function preloadNext() {
      if (list.length < 2) return;
      var next = list[(list.indexOf(current) + 1) % list.length];
      var image = $('img', slides[next]);
      if (image && image.loading === 'lazy') image.loading = 'eager';
    }

    function next() {
      if (list.length < 2) return;
      activate(list[(list.indexOf(current) + 1) % list.length]);
      preloadNext();
    }

    function start() {
      if (timer || list.length < 2) return;
      timer = window.setInterval(next, interval);
    }

    function stop() {
      window.clearInterval(timer);
      timer = null;
    }

    function setup() {
      stop();
      buildList();
      if (!list.length) return;
      if (list.indexOf(current) === -1) activate(list[0]);
      else if (currentLabel) currentLabel.textContent = pad(list.indexOf(current) + 1);
      preloadNext();
      start();
    }

    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        stop();
        activate(parseInt(dot.getAttribute('data-hero-dot'), 10));
        preloadNext();
        start();
      });
    });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        stop();
        hero.classList.remove('is-playing');
      } else {
        restartProgress();
        start();
      }
    });

    onMediaChange(desktop, setup);

    /* Theme-Editor: Block anklicken zeigt das passende Foto */
    document.addEventListener('shopify:block:select', function (event) {
      var slide = event.target.closest ? event.target.closest('[data-hero-slide]') : null;
      var index = slides.indexOf(slide);
      if (index > -1) {
        stop();
        activate(index);
      }
    });
    document.addEventListener('shopify:block:deselect', start);

    setup();
  }

  run(initHero);


  /* =======================================================
     6  SLIDER
     Aufbau im HTML: [data-slider] umschließt Kopf und Spur,
     [data-slider-track] ist die seitlich scrollbare Spur.
     Am Handy wird gewischt, am Computer per Pfeil geblättert
     oder mit der Maus gezogen.
     ======================================================= */

  function initSliders() {
    $$('[data-slider]:not([data-ready])').forEach(function (slider) {
      var track = $('[data-slider-track]', slider);
      if (!track) return;
      slider.setAttribute('data-ready', 'true');

      var prev = $('[data-slider-prev]', slider);
      var next = $('[data-slider-next]', slider);
      var progress = $('[data-slider-progress]', slider);

      function update() {
        var max = track.scrollWidth - track.clientWidth;
        var hasOverflow = max > 4;
        slider.classList.toggle('has-overflow', hasOverflow);

        if (prev) prev.disabled = track.scrollLeft <= 4;
        if (next) next.disabled = track.scrollLeft >= max - 4;

        if (progress) {
          var bar = progress.parentElement;
          var size = hasOverflow ? Math.max(0.12, track.clientWidth / track.scrollWidth) : 1;
          var offset = hasOverflow ? (track.scrollLeft / max) * (1 - size) : 0;
          progress.style.setProperty('--progress-size', size * 100 + '%');
          progress.style.setProperty('--progress-offset', offset * bar.clientWidth + 'px');
          bar.style.visibility = hasOverflow ? 'visible' : 'hidden';
        }
      }

      function step(direction) {
        var first = track.firstElementChild;
        var gap = parseFloat(window.getComputedStyle(track).columnGap) || 0;
        var itemWidth = first ? first.getBoundingClientRect().width + gap : track.clientWidth;
        var count = Math.max(1, Math.floor(track.clientWidth / itemWidth));
        scrollX(track, track.scrollLeft + direction * itemWidth * count, true);
      }

      if (prev) prev.addEventListener('click', function () { step(-1); });
      if (next) next.addEventListener('click', function () { step(1); });

      var ticking = false;
      track.addEventListener(
        'scroll',
        function () {
          if (!ticking) {
            ticking = true;
            window.requestAnimationFrame(function () {
              update();
              ticking = false;
            });
          }
        },
        { passive: true }
      );

      /* Ziehen mit der Maus */
      var dragging = false;
      var moved = false;
      var startX = 0;
      var startScroll = 0;

      track.addEventListener('mousedown', function (event) {
        if (event.button !== 0) return;
        dragging = true;
        moved = false;
        startX = event.pageX;
        startScroll = track.scrollLeft;
      });

      window.addEventListener('mousemove', function (event) {
        if (!dragging) return;
        var distance = event.pageX - startX;
        if (!moved && Math.abs(distance) > 6) {
          moved = true;
          track.classList.add('is-dragging');
        }
        if (moved) {
          event.preventDefault();
          track.scrollLeft = startScroll - distance;
        }
      });

      window.addEventListener('mouseup', function () {
        if (!dragging) return;
        dragging = false;
        if (moved) {
          /* Klick nach dem Ziehen unterdrücken, dann Einrasten wieder zulassen */
          window.setTimeout(function () {
            track.classList.remove('is-dragging');
          }, 0);
        }
      });

      track.addEventListener(
        'click',
        function (event) {
          if (moved) {
            event.preventDefault();
            event.stopPropagation();
            moved = false;
          }
        },
        true
      );

      /* Bilder sollen sich nicht als "Geisterbild" mitziehen lassen */
      track.addEventListener('dragstart', function (event) {
        event.preventDefault();
      });

      window.addEventListener('resize', update);
      window.addEventListener('load', update);
      update();
    });
  }

  run(initSliders);


  /* =======================================================
     7  KUNDENSTIMMEN
     ======================================================= */

  function initVoices() {
    $$('[data-voices]:not([data-ready])').forEach(function (section) {
      section.setAttribute('data-ready', 'true');
      var slides = $$('[data-voice]', section);
      var dots = $$('[data-voice-dot]', section);
      if (slides.length < 2) return;

      var current = 0;
      var timer = null;

      function show(index) {
        current = (index + slides.length) % slides.length;
        slides.forEach(function (slide, i) {
          slide.classList.toggle('is-active', i === current);
          slide.setAttribute('aria-hidden', i === current ? 'false' : 'true');
        });
        dots.forEach(function (dot, i) {
          dot.classList.toggle('is-active', i === current);
        });
      }

      function start() {
        if (reduceMotion || timer) return;
        timer = window.setInterval(function () {
          show(current + 1);
        }, 6500);
      }

      function stop() {
        window.clearInterval(timer);
        timer = null;
      }

      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () {
          stop();
          show(i);
          start();
        });
      });

      section.addEventListener('mouseenter', stop);
      section.addEventListener('mouseleave', start);
      section.addEventListener('focusin', stop);
      section.addEventListener('focusout', start);

      show(0);
      start();
    });
  }

  run(initVoices);


  /* Theme-Editor: Wird ein Abschnitt neu geladen, bekommt er seine
     Slider und Slideshows zurück. */
  document.addEventListener('shopify:section:load', function () {
    run(initHero);
    run(initSliders);
    run(initVoices);
  });


  /* =======================================================
     8  AUTOMATISCHER BILDWECHSEL (NUR HANDY)
     Produkte mit mehreren Varianten-Bildern (siehe
     snippets/product-image-cycle.liquid) wechseln auf dem
     Handy alle 5 Sekunden sanft zwischen ihnen. Am Computer
     bleibt das erste Bild stehen – dort gibt es stattdessen
     die Vorschau beim Überfahren.
     ======================================================= */

  run(function initImageCycle() {
    var mobile = window.matchMedia('(max-width: 749px)');
    var motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    var timers = new WeakMap();
    var INTERVAL = 5000;

    function startCycle(container) {
      if (timers.has(container)) return;
      var frames = $$('.Leveyn-cycle-frame', container);
      if (frames.length < 2) return;

      var index = 0;
      frames.forEach(function (frame, i) {
        if (frame.classList.contains('is-active')) index = i;
      });

      var id = window.setInterval(function () {
        frames[index].classList.remove('is-active');
        index = (index + 1) % frames.length;
        frames[index].classList.add('is-active');
      }, INTERVAL);
      timers.set(container, id);
    }

    function stopCycle(container) {
      var id = timers.get(container);
      if (id) {
        window.clearInterval(id);
        timers.delete(container);
      }
      $$('.Leveyn-cycle-frame', container).forEach(function (frame, i) {
        frame.classList.toggle('is-active', i === 0);
      });
    }

    function apply() {
      $$('.Leveyn-image-cycle').forEach(function (container) {
        if (mobile.matches && !motion.matches) startCycle(container);
        else stopCycle(container);
      });
    }

    apply();
    onMediaChange(mobile, apply);
    onMediaChange(motion, apply);
  });


  /* =======================================================
     9  WARENKORB: ZÄHLER, SCHNELLKAUF, WARENKORB-FENSTER
     ======================================================= */

  function updateCartCount(count) {
    $$('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.classList.toggle('is-empty', count === 0);
    });
    $$('[data-cart-count-text]').forEach(function (el) {
      el.textContent = count + ' ' + (count === 1 ? strings.itemOne : strings.itemOther);
    });
  }

  var drawer = run(function initDrawer() {
    var element = $('[data-cart-drawer]');
    if (!element || !config.cartDrawer) return null;

    var panel = $('.lv-drawer__panel', element);
    var list = $('[data-cart-items]', element);
    var foot = $('[data-cart-foot]', element);
    var total = $('[data-cart-total]', element);
    var lastFocus = null;

    function imageUrl(url, width) {
      if (!url) return '';
      return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
    }

    function render(cart) {
      updateCartCount(cart.item_count);

      if (!cart.items.length) {
        list.innerHTML = '<p class="lv-drawer__empty">' + escapeHtml(strings.empty) + '</p>';
        foot.hidden = true;
        return;
      }

      list.innerHTML = cart.items
        .map(function (item) {
          var options = (item.options_with_values || [])
            .filter(function (option) {
              return option.value !== 'Default Title';
            })
            .map(function (option) {
              return escapeHtml(option.name) + ': ' + escapeHtml(option.value);
            });
          options.push(escapeHtml(strings.quantity) + ': ' + item.quantity);

          return (
            '<div class="lv-drawer__item">' +
            '<a class="lv-drawer__thumb" href="' + escapeHtml(item.url) + '" tabindex="-1" aria-hidden="true">' +
            (item.image ? '<img src="' + escapeHtml(imageUrl(item.image, 240)) + '" alt="" loading="lazy" width="120" height="120">' : '') +
            '</a>' +
            '<div class="lv-drawer__info">' +
            '<a class="lv-drawer__name" href="' + escapeHtml(item.url) + '">' + escapeHtml(item.product_title) + '</a>' +
            '<p class="lv-drawer__meta">' + options.join(', ') + '</p>' +
            '<div class="lv-drawer__row">' +
            '<span class="lv-drawer__price">' + formatMoney(item.final_line_price) + '</span>' +
            '<button type="button" class="lv-drawer__remove" data-cart-remove="' + escapeHtml(item.key) + '">' + escapeHtml(strings.remove) + '</button>' +
            '</div>' +
            '</div>' +
            '</div>'
          );
        })
        .join('');

      total.textContent = formatMoney(cart.total_price);
      foot.hidden = false;
    }

    /* Nach dem Neuaufbau der Liste den Fokus im Fenster halten */
    function keepFocus() {
      if (element.classList.contains('is-open') && !panel.contains(document.activeElement)) panel.focus();
    }

    function refresh() {
      element.classList.add('is-loading');
      return getCart()
        .then(render)
        .catch(function () {
          /* Fällt der Abruf aus, bleibt die Warenkorb-Seite als sicherer Weg */
          window.location.href = config.routes.cart;
        })
        .then(function () {
          element.classList.remove('is-loading');
        });
    }

    function open() {
      lastFocus = document.activeElement;
      element.hidden = false;
      window.requestAnimationFrame(function () {
        element.classList.add('is-open');
      });
      document.body.classList.add('lv-is-locked');
      panel.focus();
      return refresh();
    }

    function close() {
      element.classList.remove('is-open');
      document.body.classList.remove('lv-is-locked');
      window.setTimeout(function () {
        if (!element.classList.contains('is-open')) element.hidden = true;
      }, 620);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    $$('[data-cart-close]', element).forEach(function (el) {
      el.addEventListener('click', close);
    });

    /* Escape schließt auch dann, wenn der Fokus gerade nirgends liegt
       (z. B. nachdem ein Artikel entfernt wurde) */
    document.addEventListener('keydown', function (event) {
      if (!element.classList.contains('is-open')) return;
      if (event.key === 'Escape') close();
      trapFocus(panel, event);
    });

    list.addEventListener('click', function (event) {
      var button = event.target.closest('[data-cart-remove]');
      if (!button) return;
      element.classList.add('is-loading');
      postJson('cart/change.js', { id: button.getAttribute('data-cart-remove'), quantity: 0 })
        .then(render)
        .catch(refresh)
        .then(function () {
          element.classList.remove('is-loading');
          keepFocus();
        });
    });

    $$('[data-cart-open]').forEach(function (link) {
      link.addEventListener('click', function (event) {
        /* Mit gedrückter Zusatztaste soll der Link normal im neuen Tab öffnen */
        if (event.metaKey || event.ctrlKey || event.shiftKey) return;
        event.preventDefault();
        open();
      });
    });

    return { open: open };
  });

  function afterAdd() {
    if (drawer) return drawer.open();
    return getCart().then(function (cart) {
      updateCartCount(cart.item_count);
    });
  }

  /* Schnellkauf auf Produktkarten */
  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-quick-add]');
    if (!button || button.disabled) return;
    event.preventDefault();

    var variantId = button.getAttribute('data-variant');
    if (!variantId) return;

    var label = button.getAttribute('data-label') || button.textContent.trim();
    button.setAttribute('data-label', label);
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = '…';

    postJson('cart/add.js', { items: [{ id: Number(variantId), quantity: 1 }] })
      .then(function () {
        button.textContent = strings.added;
        button.classList.add('is-success');
        return afterAdd();
      })
      .catch(function (error) {
        button.textContent = error.message && error.message.length < 34 ? error.message : strings.unavailable;
        button.classList.add('is-error');
      })
      .then(function () {
        window.setTimeout(function () {
          button.textContent = label;
          button.disabled = false;
          button.removeAttribute('aria-busy');
          button.classList.remove('is-success', 'is-error');
        }, 2200);
      });
  });


  /* =======================================================
     10  KATEGORIESEITE: SORTIERUNG
     Wechsel lädt die Seite mit neuem sort_by neu. "view"
     (Produkte pro Seite) bleibt erhalten, "page" wird
     zurückgesetzt, damit man wieder auf Seite 1 startet.
     ======================================================= */

  run(function initSort() {
    var select = $('[data-sort-select]');
    if (!select) return;

    select.addEventListener('change', function () {
      var params = new URLSearchParams(window.location.search);
      /* "Empfohlen" hat keinen Wert: Dann entfällt sort_by ganz. */
      if (select.value) params.set('sort_by', select.value);
      else params.delete('sort_by');
      params.delete('page');
      var query = params.toString();
      window.location.href = window.location.pathname + (query ? '?' + query : '');
    });
  });

  /* "Weitere anzeigen" in der empfohlenen Reihenfolge: Alle Karten der
     Kategorie stehen schon auf der Seite, die späteren sind nur
     ausgeblendet. Der Button zeigt jeweils die nächsten. Wie viele schon
     offen sind, merkt sich die Seite, damit man nach "Zurück" vom
     Produkt wieder an derselben Stelle landet. */
  run(function initShowMore() {
    var button = $('[data-more-button]');
    var grid = $('[data-more-grid]');
    if (!button || !grid) return;

    var wrap = $('[data-more-wrap]');
    var counter = $('[data-more-shown]');
    var step = parseInt(button.getAttribute('data-step'), 10) || 36;
    var total = grid.children.length;
    var key = 'lv-more:' + window.location.pathname + window.location.search;

    function reveal(count) {
      var hidden = $$('[data-more]', grid);
      hidden.slice(0, count).forEach(function (item) {
        item.removeAttribute('data-more');
      });
      var left = $$('[data-more]', grid).length;
      if (counter) counter.textContent = String(total - left);
      if (wrap && left === 0) wrap.hidden = true;
      try {
        window.sessionStorage.setItem(key, String(total - left));
      } catch (error) {}
    }

    try {
      var saved = parseInt(window.sessionStorage.getItem(key), 10);
      if (saved > step) reveal(saved - step);
    } catch (error) {}

    button.addEventListener('click', function () {
      reveal(step);
    });
  });


  /* =======================================================
     11  PRODUKTSEITE
     ======================================================= */

  run(function initProduct() {
    var product = $('[data-product]');
    if (!product) return;

    var variants = [];
    try {
      variants = JSON.parse($('[data-product-variants]', product).textContent);
    } catch (error) {
      variants = [];
    }

    var form = $('[data-product-form]', product);
    var idInput = $('[data-variant-id]', product);
    var priceEls = $$('[data-product-price]', product);
    var compareEls = $$('[data-product-compare]', product);
    var addButtons = $$('[data-add-button]', product);
    var optionGroups = $$('[data-option]', product);

    var labelAdd = (addButtons[0] && addButtons[0].getAttribute('data-label-add')) || strings.addToCart;
    var labelSoldOut = (addButtons[0] && addButtons[0].getAttribute('data-label-sold-out')) || strings.soldOut;

    /* ---------- Galerie ---------- */

    var track = $('[data-gallery-track]', product);
    var slides = track ? $$('[data-media-id]', track) : [];
    var thumbs = $$('[data-gallery-thumb]', product);
    var counter = $('[data-gallery-current]', product);
    var prev = $('[data-gallery-prev]', product);
    var next = $('[data-gallery-next]', product);
    var activeIndex = 0;
    var programmatic = false;
    var programmaticTimer = null;

    function markActive(index) {
      activeIndex = index;
      thumbs.forEach(function (thumb, i) {
        var isActive = i === index;
        thumb.classList.toggle('is-active', isActive);
        if (isActive) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      if (counter) counter.textContent = index + 1;
      if (prev) prev.disabled = index === 0;
      if (next) next.disabled = index === slides.length - 1;
    }

    function goTo(index, smooth) {
      if (!track || !slides.length) return;
      index = Math.max(0, Math.min(slides.length - 1, index));
      programmatic = true;
      window.clearTimeout(programmaticTimer);
      programmaticTimer = window.setTimeout(function () {
        programmatic = false;
      }, 700);
      scrollX(track, slides[index].offsetLeft - track.offsetLeft, smooth);
      markActive(index);
    }

    function indexOfMedia(mediaId) {
      var found = -1;
      slides.forEach(function (slide, i) {
        if (String(slide.getAttribute('data-media-id')) === String(mediaId)) found = i;
      });
      return found;
    }

    /* Bild -> passende Variante: wer durch die Bilder blättert,
       wählt damit die Variante, der das Bild zugeordnet ist. */
    function syncVariantToMedia(index) {
      var mediaId = slides[index] && slides[index].getAttribute('data-media-id');
      if (!mediaId) return;
      var match = variants.filter(function (variant) {
        return variant.featured_media && String(variant.featured_media.id) === String(mediaId);
      })[0];
      if (!match) return;
      setOptions(match.options);
      updateVariant(false);
    }

    if (track && slides.length) {
      var scrollTimer = null;
      track.addEventListener(
        'scroll',
        function () {
          window.clearTimeout(scrollTimer);
          scrollTimer = window.setTimeout(function () {
            var index = Math.round(track.scrollLeft / track.clientWidth);
            index = Math.max(0, Math.min(slides.length - 1, index));
            if (index !== activeIndex) {
              markActive(index);
              if (!programmatic) syncVariantToMedia(index);
            }
          }, 90);
        },
        { passive: true }
      );

      if (prev) prev.addEventListener('click', function () {
        goTo(activeIndex - 1, true);
        syncVariantToMedia(activeIndex);
      });
      if (next) next.addEventListener('click', function () {
        goTo(activeIndex + 1, true);
        syncVariantToMedia(activeIndex);
      });

      thumbs.forEach(function (thumb, i) {
        thumb.addEventListener('click', function () {
          goTo(i, true);
          syncVariantToMedia(i);
        });
      });

      window.addEventListener('resize', function () {
        goTo(activeIndex, false);
      });
    }

    /* ---------- Varianten ---------- */

    function selectedOptions() {
      return optionGroups.map(function (group) {
        var select = $('select', group);
        if (select) return select.value;
        var checked = $('input:checked', group);
        return checked ? checked.value : null;
      });
    }

    function setOptions(values) {
      optionGroups.forEach(function (group, i) {
        var select = $('select', group);
        if (select) {
          select.value = values[i];
        } else {
          $$('input', group).forEach(function (input) {
            input.checked = input.value === values[i];
          });
        }
        var current = $('[data-option-current]', group);
        if (current) current.textContent = values[i];
      });
    }

    function findVariant(options) {
      return variants.filter(function (variant) {
        return variant.options.every(function (value, i) {
          return value === options[i];
        });
      })[0];
    }

    /* Werte, die es in der aktuellen Kombination nicht (mehr) gibt, durchstreichen */
    function markAvailability(options) {
      optionGroups.forEach(function (group, groupIndex) {
        $$('input', group).forEach(function (input) {
          var test = options.slice();
          test[groupIndex] = input.value;
          var variant = findVariant(test);
          input.classList.toggle('is-unavailable', !variant || !variant.available);
        });
      });
    }

    function updateVariant(moveGallery) {
      var options = selectedOptions();
      var variant = optionGroups.length ? findVariant(options) : variants[0];

      optionGroups.forEach(function (group, i) {
        var current = $('[data-option-current]', group);
        if (current) current.textContent = options[i];
      });
      markAvailability(options);

      if (!variant) {
        addButtons.forEach(function (button) {
          button.disabled = true;
          button.textContent = strings.unavailable;
        });
        return;
      }

      if (idInput) idInput.value = variant.id;

      priceEls.forEach(function (el) {
        el.textContent = formatMoney(variant.price);
      });

      compareEls.forEach(function (el) {
        var show = variant.compare_at_price && variant.compare_at_price > variant.price;
        el.textContent = show ? formatMoney(variant.compare_at_price) : '';
        el.hidden = !show;
      });

      addButtons.forEach(function (button) {
        button.disabled = !variant.available;
        button.textContent = variant.available ? labelAdd : labelSoldOut;
      });

      /* Adresse aktuell halten, damit der Link zur gewählten Variante teilbar ist */
      if (window.history && window.history.replaceState && optionGroups.length) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      if (moveGallery !== false && variant.featured_media) {
        var index = indexOfMedia(variant.featured_media.id);
        if (index > -1 && index !== activeIndex) goTo(index, true);
      }
    }

    optionGroups.forEach(function (group) {
      group.addEventListener('change', function () {
        updateVariant(true);
      });
    });

    /* ---------- In den Warenkorb, ohne die Seite neu zu laden ---------- */

    if (form) {
      form.addEventListener('submit', function (event) {
        event.preventDefault();
        var variantId = idInput ? idInput.value : null;
        if (!variantId || (addButtons[0] && addButtons[0].disabled)) return;

        addButtons.forEach(function (button) {
          button.disabled = true;
          button.setAttribute('aria-busy', 'true');
          button.textContent = '…';
        });

        postJson('cart/add.js', { items: [{ id: Number(variantId), quantity: 1 }] })
          .then(function () {
            addButtons.forEach(function (button) {
              button.textContent = strings.added;
            });
            return afterAdd();
          })
          .catch(function (error) {
            addButtons.forEach(function (button) {
              button.textContent = error.message && error.message.length < 40 ? error.message : strings.unavailable;
            });
          })
          .then(function () {
            window.setTimeout(function () {
              addButtons.forEach(function (button) {
                button.removeAttribute('aria-busy');
              });
              updateVariant(false);
            }, 2000);
          });
      });
    }

    /* ---------- Kaufleiste am unteren Bildschirmrand (Handy) ---------- */

    var bar = $('[data-buy-bar]', product);
    var mainButton = $('[data-add-button]', form || product);
    if (bar && mainButton) {
      var barTicking = false;

      /* Leiste zeigen, sobald der Button nach oben aus dem Bild gescrollt ist */
      var updateBar = function () {
        bar.classList.toggle('is-visible', mainButton.getBoundingClientRect().bottom < 0);
        barTicking = false;
      };

      window.addEventListener(
        'scroll',
        function () {
          if (!barTicking) {
            barTicking = true;
            window.requestAnimationFrame(updateBar);
          }
        },
        { passive: true }
      );
      updateBar();
    }

    /* ---------- Start ---------- */

    var startIndex = 0;
    slides.forEach(function (slide, i) {
      if (slide.hasAttribute('data-initial')) startIndex = i;
    });
    if (slides.length) goTo(startIndex, false);
    updateVariant(false);
  });


  /* =======================================================
     12  WARENKORB-SEITE
     Nach jeder Änderung wird die Seite neu geladen. So stehen
     alle Preise, Rabatte und Zeilen garantiert so da, wie
     Shopify sie berechnet.
     ======================================================= */

  run(function initCartPage() {
    var page = $('[data-cart-page]');
    if (!page) return;

    function change(line, quantity) {
      page.classList.add('is-loading');
      postJson('cart/change.js', { line: line, quantity: quantity })
        .then(function (cart) {
          updateCartCount(cart.item_count);
          window.location.reload();
        })
        .catch(function () {
          page.classList.remove('is-loading');
        });
    }

    $$('[data-cart-line]', page).forEach(function (row) {
      var line = parseInt(row.getAttribute('data-cart-line'), 10);
      var quantity = parseInt(row.getAttribute('data-quantity'), 10) || 1;

      $$('[data-cart-action]', row).forEach(function (button) {
        button.addEventListener('click', function () {
          var action = button.getAttribute('data-cart-action');
          if (action === 'decrease' && quantity > 1) change(line, quantity - 1);
          if (action === 'increase') change(line, quantity + 1);
          if (action === 'remove') change(line, 0);
        });
      });
    });

    /* "Zur Kasse": Rückmeldung, während Shopify die Kasse lädt */
    var checkout = $('[data-checkout]', page);
    if (checkout) {
      checkout.addEventListener('click', function () {
        var label = $('span', checkout);
        if (label) label.textContent = checkout.getAttribute('data-loading-label') || label.textContent;
      });
    }

    /* Gutschein- / Rabattcode
       Shopify prüft Rabattcodes ausschließlich an der Kasse. Der
       Code wird deshalb als "?discount=" an die Kassen-Adresse
       angehängt – der von Shopify vorgesehene Weg, der mit jedem
       im Adminbereich angelegten Code funktioniert. */
    var discountInput = $('[data-discount-input]', page);
    var discountApply = $('[data-discount-apply]', page);
    var cartForm = $('[data-cart-form]', page);

    function checkoutUrl(code) {
      var base = root + 'checkout';
      return code ? base + '?discount=' + encodeURIComponent(code) : base;
    }

    if (discountInput && discountApply) {
      discountApply.addEventListener('click', function () {
        var code = discountInput.value.trim();
        if (!code) {
          discountInput.focus();
          return;
        }
        discountApply.disabled = true;
        window.location.href = checkoutUrl(code);
      });

      discountInput.addEventListener('keydown', function (event) {
        if (event.key === 'Enter') {
          event.preventDefault();
          discountApply.click();
        }
      });
    }

    if (cartForm && discountInput) {
      cartForm.addEventListener('submit', function (event) {
        var code = discountInput.value.trim();
        if (code) {
          event.preventDefault();
          window.location.href = checkoutUrl(code);
        }
      });
    }
  });


  /* =======================================================
     13  SEITE "STORNIERUNG, WIDERRUF UND RÜCKGABE": REITER
     ======================================================= */

  run(function initSupportTabs() {
    $$('.Leveyn-support-page').forEach(function (page) {
      var tabs = $$('[data-support-tab]', page);
      var panels = $$('[data-support-panel]', page);

      tabs.forEach(function (tab) {
        tab.addEventListener('click', function () {
          var target = tab.getAttribute('data-support-tab');
          tabs.forEach(function (other) {
            var isTarget = other === tab;
            other.classList.toggle('is-active', isTarget);
            other.setAttribute('aria-selected', isTarget ? 'true' : 'false');
          });
          panels.forEach(function (panel) {
            panel.classList.toggle('is-active', panel.getAttribute('data-support-panel') === target);
          });
        });
      });
    });
  });
})();
